// ArChat favoritePeerId
// Created: 15-04-2026 23:59:00
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * @file favoritePeerId.js
 * @description Resolución del ID de usuario favorito (peer) desde filas API / almacenamiento local.
 * La fila `usuarios_favoritos` expone `id` como PK de la fila; el ID del contacto favorito es `usuario_favorito_id`.
 * Usar `id` como peer provoca estrellas y filtros incoherentes con `resolveContactId` en la UI.
 * @author SIVOL® - Aldo Frias - Argentina
 */

import { resolveContactId, resolvePeerUsuarioIdForPresence } from './contactIdResolver.js';
import { normalizeContact } from './contactHelpers.js';

/**
 * @param {Record<string, unknown>|null|undefined} row - Elemento de GET /api/favoritos.php `data[]` o equivalente en localStorage.
 * @returns {number} ID del usuario favorito (peer) o 0 si no aplica.
 */
export function extractFavoritePeerUserId(row) {
  if (row == null || typeof row !== 'object') return 0;
  const candidates = [
    row.usuario_favorito_id,
    row.favorited_user_id,
    row.peer_user_id,
    row.contact_user_id,
    row.usuario_id,
  ];
  for (const raw of candidates) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

/** Lectura diagnóstica / futuros merges; el filtro usa solo `favoriteIds` (GET favoritos). */
export function readFavoriteFlagFromContactRow(row) {
  if (row == null || typeof row !== 'object') return null;
  const keys = [
    'es_favorito',
    'is_favorite',
    'isFavorite',
    'favorito',
    'favorite',
    'usuario_favorito',
    'favorito_usuario',
    'is_starred',
    'starred',
  ];
  const truthyStr = new Set(['1', 'true', 'yes', 'y', 'si', 'sí']);
  const falsyStr = new Set(['0', 'false', 'no', '']);
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(row, k)) continue;
    const v = row[k];
    if (v === null || v === undefined) continue;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number' && Number.isFinite(v)) {
      if (v === 1) return true;
      if (v === 0) return false;
    }
    const s = String(v).trim().toLowerCase();
    if (truthyStr.has(s)) return true;
    if (falsyStr.has(s)) return false;
  }
  return null;
}

/**
 * ID del peer alineado con `usuario_favorito_id` / toggleFavorite / `favoriteIds`.
 * No usa `usuario_id` del dueño de fila (puede coincidir en varias filas) ni prioriza PK de fila sobre el peer.
 *
 * @param {Record<string, unknown>|null|undefined} contact - Fila de contacto/conversación normalizada o cruda.
 * @param {number|string|null|undefined} viewerUsuarioId - Usuario logueado (excluir como peer).
 * @returns {number|null} ID del usuario peer o null.
 */
export function resolveFavoritePeerUserId(contact, viewerUsuarioId) {
  if (contact == null || typeof contact !== 'object') return null;
  const peer = resolvePeerUsuarioIdForPresence(contact, viewerUsuarioId);
  if (peer != null && peer > 0) return peer;
  const fallback = resolveContactId(contact);
  const n = Number(fallback);
  const v =
    viewerUsuarioId != null && viewerUsuarioId !== '' ? Number(viewerUsuarioId) : null;
  if (!Number.isFinite(n) || n <= 0) return null;
  if (v != null && Number.isFinite(v) && v > 0 && n === v) return null;
  return n;
}

/**
 * Una sola fuente de verdad para: key React, `contactId`/`isFavorite`, toggle y dedupe de lista.
 * `displayContactRows` antes usaba `item` crudo; `ContactList` usaba `normalizeContact(item)` → mismas
 * filas podían resolver a distinto peer/key y colisionar (duplicar keys → clic en fila N afecta fila 1).
 *
 * @param {Record<string, unknown>|null|undefined} item - Fila de contacto/conversación
 * @param {number|string|null|undefined} viewerUsuarioId - Viewer (Auth)
 * @param {number} [rowIndex] - Índice estable solo si falta peer/conversación (evitar key duplicada "")
 * @returns {{ normalized: object, peerIdStr: string, reactKeySuffix: string }}
 */
export function getFavoriteRowCanonicalIds(item, viewerUsuarioId, rowIndex = 0) {
  const normalized = normalizeContact(item);
  const peer = resolveFavoritePeerUserId(normalized, viewerUsuarioId);
  const fallback = resolveContactId(normalized);
  const raw = peer != null && peer > 0 ? peer : fallback;
  let peerIdStr = '';
  const num = Number(raw);
  if (Number.isFinite(num) && num > 0) {
    const v = viewerUsuarioId != null && viewerUsuarioId !== '' ? Number(viewerUsuarioId) : null;
    if (v == null || !Number.isFinite(v) || num !== v) {
      peerIdStr = String(num);
    }
  }
  const conv =
    normalized.conversacion_existente_id ??
    normalized.conversacion_id ??
    null;
  const convStr = conv != null && String(conv).trim() !== '' ? String(conv) : '';
  const invId =
    normalized.invitacion_id ??
    normalized.invitation_id ??
    null;
  const invStr = invId != null && String(invId).trim() !== '' ? String(invId) : '';

  /** Discriminador de fila API (no es el peer): desagrega filas aunque compartan peer/conversación en merges. */
  const rowDiscrimRaw =
    normalized.usuario_contacto_id ??
    normalized.contacto_id ??
    normalized.conversacion_existente_id ??
    normalized.conversacion_id ??
    normalized.id ??
    null;
  const rowDiscrim =
    rowDiscrimRaw != null && String(rowDiscrimRaw).trim() !== ''
      ? String(rowDiscrimRaw)
      : '';
  const rowExtra = rowDiscrim ? `-r${rowDiscrim}` : '';

  const reactKeySuffix =
    peerIdStr !== ''
      ? `${peerIdStr}${convStr ? `-c${convStr}` : ''}${rowExtra}`
      : convStr !== ''
        ? `conv-${convStr}`
        : invStr !== ''
          ? `inv-${invStr}`
          : `idx-${rowIndex}`;

  return { normalized, peerIdStr, reactKeySuffix };
}
